import { useEffect, useState } from "react";
import { useBreakpointValue, Box, Flex, Slide, Link, VStack, Divider } from "@chakra-ui/react";
import { Airhelp as AirHelpLogo } from "@airhelp/react";
import FlagBox from "components/LanguageSwitcher/FlagBox";
import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useLanguage } from "contexts/LocalizationContext";
import type { ModalContextProps } from "contexts/ModalContext";
import { useModal } from "contexts/ModalContext";
import { useFlagBoxSettings } from "contexts/SettingsContext";
import { useMobileMenuStore } from "stores/MobileMenuStore";
import { useReturningUsersNotification, useDetectScrollDirection } from "hooks";
import throttle from "lodash/throttle";
import NextLink from "next/link";
import ReturningUsersNotification from "../../ReturningUsersNotification";
import { TOP_MENU_LANG_MODAL_OPEN_EVENT_UA, TOP_MENU_LANG_MODAL_OPEN_EVENT } from "../../../trackingConsts";
import type { HeaderNavProps } from "../HeaderNav.d";
import { useTopMenuExperiment } from "./useTopMenuExperiment";
import { MobileMenuTrigger } from "./MobileMenuTrigger";

export const HeaderNavExperiment = ({
  logo: Logo = AirHelpLogo,
  menuItems,
  headerButtons,
  subMenu = null,
}: HeaderNavProps) => {
  const [slideOpen, setSlideOpen] = useState(true);
  const [isScrolled, setIsScrolled] = useState(false);
  const [position, setPosition] = useState<"sticky" | "fixed">("fixed");
  const language = useLanguage();
  const { openModal } = useModal();
  const { isOpen: isMobileMenuOpen, close: closeMobileMenu } = useMobileMenuStore();
  const hideFlagBox = useFlagBoxSettings();
  const { isOpen: isNotificationVisible } = useReturningUsersNotification();
  const experimentVariant = useTopMenuExperiment();
  const shouldHideSecondRow = !slideOpen && experimentVariant === 2;

  const scrollDirection = useDetectScrollDirection("80");

  const navBoxShadow = isScrolled ? "1px 4px 10px rgba(203, 202, 209, 0.25)" : "none";

  const handleLanguageSwitcherClick: ModalContextProps["openModal"] = (modal, size) => {
    closeMobileMenu();
    openModal(modal, size);
  };

  const navOnScroll = () => {
    setIsScrolled(window.pageYOffset > 0);
  };

  const throttledResizeListener = throttle(navOnScroll, 50);

  useEffect(() => {
    setPosition(isNotificationVisible ? "sticky" : "fixed");
  }, [isNotificationVisible]);

  useEffect(() => {
    setSlideOpen(scrollDirection !== "down");

    window.addEventListener("scroll", throttledResizeListener);

    return () => window.removeEventListener("scroll", throttledResizeListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollDirection]);

  return (
    <>
      {/* empty div to move page contents lower only for pages where experiment is run */}
      <Box h={{ base: 0, lg: isNotificationVisible ? 0 : 12 }} />
      <ReturningUsersNotification />
      <Slide
        initial="visible"
        direction="top"
        in={useBreakpointValue({ base: slideOpen, lg: true })}
        style={{ zIndex: isMobileMenuOpen ? 4000 : 3, position }}
      >
        <Box width="full" boxShadow={navBoxShadow} backgroundColor="white">
          <VStack
            width="full"
            maxW={{ base: "full", xl: MAX_GRID_WIDTH }}
            mx="auto"
            px={{ base: 4, xl: 0 }}
            py={4}
            as="nav"
            role="menubar"
          >
            <Flex width="full" h={{ base: 8, lg: "auto" }} justify={{ base: "space-between", lg: "unset" }}>
              <Link as={NextLink} href={`/${language}/`} mr="auto" passHref>
                <Logo color="primary.900" minW={{ base: "94px", lg: "118px" }} minH={{ base: "32px", lg: "40px" }} />
              </Link>
              {!hideFlagBox && (
                <Flex mr={{ base: 1, lg: 5 }} alignItems="center">
                  <FlagBox
                    openModal={handleLanguageSwitcherClick}
                    gaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT}
                    uaEvent={TOP_MENU_LANG_MODAL_OPEN_EVENT_UA}
                  />
                  <Divider
                    orientation="vertical"
                    h={12}
                    borderColor="greyscale.400"
                    display={{ base: "none", lg: "initial" }}
                  />
                </Flex>
              )}
              {subMenu && (
                <Box h={8} display={{ base: "block", md: "none" }} alignItems="center">
                  {subMenu}
                </Box>
              )}
              <Box display={{ base: "none", lg: "flex" }}>{headerButtons}</Box>
              <MobileMenuTrigger menuItems={menuItems} headerButtons={headerButtons} />
            </Flex>
            <Flex
              display={{ base: "none", lg: "flex" }}
              pt={shouldHideSecondRow ? 0 : 8}
              w="full"
              gap={7}
              maxHeight={shouldHideSecondRow ? "0" : "80px"}
              overflow="hidden"
              transition="all 0.2s linear"
              sx={{ marginTop: "0 !important" }}
            >
              {menuItems}
            </Flex>
          </VStack>
        </Box>
      </Slide>
    </>
  );
};
